import { View, TouchableOpacity, KeyboardAvoidingView } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import { useUser, useUserProfilePhoneNumberMutation } from "@/queries/user"
import { router } from "expo-router"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { type Dispatch, type SetStateAction, useState } from "react"
import { formatPhoneNumberString } from "@/utils/StringUtils"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText from "@/components/standard/BFText"

interface EditPhoneNumberFormProps {
  phoneNumber: string | null | undefined
  phoneNumberError: string | undefined
  setPhoneNumber: Dispatch<SetStateAction<string | null | undefined>>
  disabled?: boolean
}

const EditPhoneNumberForm: React.FC<EditPhoneNumberFormProps> = ({
  phoneNumber,
  setPhoneNumber,
  phoneNumberError,
  disabled = false,
}: EditPhoneNumberFormProps) => {
  const onPhoneChange = (val: string | null | undefined) => {
    setPhoneNumber(formatPhoneNumberString(val))
  }

  const formattedPhone = formatPhoneNumberString(phoneNumber)

  return (
    <View className="flex flex-col justify-start flex-1">
      <View className="flex flex-col mt-6 shrink-0">
        <View className="py-1">
          <BFText className="text-midnight-blue font-spacegrotesk">
            Phone Number (optional)
          </BFText>
        </View>
        <View className="py-1">
          <FeedbackTextInput
            value={formattedPhone || undefined}
            onChange={onPhoneChange}
            error={phoneNumberError}
            disabled={disabled}
          />
        </View>
      </View>
    </View>
  )
}

const onBack = () => {
  router.back()
}

export default function EditPhoneNumberScreen() {
  const { data, isLoading } = useUser()
  const updateProfilePhoneNumberMutation = useUserProfilePhoneNumberMutation()
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountEditPhoneNumberViewed()
  }, [])

  const [phoneNumber, setPhoneNumber] = useState<string | null | undefined>(
    undefined
  )

  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(
    undefined
  )

  if (isLoading || !data?.me) {
    return
  }

  const { me: userProfile } = data

  const scratchPhoneNumber = phoneNumber ?? userProfile.phoneNumber

  const isDisabled = isLoading || updateProfilePhoneNumberMutation.isPending

  const validate = () => {
    let isValid = true
    if (
      typeof scratchPhoneNumber === "undefined" ||
      scratchPhoneNumber == null ||
      scratchPhoneNumber.length === 0
    ) {
      setPhoneNumberError("Phone number is invalid.")
      isValid = false
    } else {
      setPhoneNumberError(undefined)
    }

    return isValid
  }

  const onSave = () => {
    if (isDisabled || !scratchPhoneNumber) {
      return
    }

    if (validate()) {
      updateProfilePhoneNumberMutation.mutate(
        {
          phoneNumber: scratchPhoneNumber,
        },
        {
          onSuccess: (data) => {
            if (data?.__typename === "UserProfileUpdateSuccess") {
              router.navigate("/account/personal-info/")
              return
            }

            if (data?.__typename === "UserProfileUpdateError") {
              setPhoneNumberError(data.errorText)
            }
          },
          onError: (error) => {
            console.log(error)
          },
        }
      )
    }
  }

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-white"
      behavior="padding"
      style={{
        paddingTop: isWeb ? insets.top + 10 : insets.top,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Edit Phone
        </BFText>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <EditPhoneNumberForm
          phoneNumber={scratchPhoneNumber}
          setPhoneNumber={setPhoneNumber}
          phoneNumberError={phoneNumberError}
          disabled={isDisabled}
        />
        <View className="flex flex-col flex-1 max-h-14">
          <PrimaryButton testID={"save-button"} title="Save" onPress={onSave} />
        </View>
      </View>
    </KeyboardAvoidingView>
  )
}
